import React, { useRef } from 'react';
import { Select } from '@cubeyou-team/hx-react-components';

type SelectProps = React.ComponentProps<typeof Select>;

/**
 * The design-system Select defaults to `setMaxHeight: 'none'`, so a long list
 * (e.g. countries) renders at full height. The dropdown is absolutely
 * positioned, so it grows the document's scroll height without growing the
 * layout's background, exposing the white page canvas below it.
 */
const DEFAULT_MAX_HEIGHT = '250px';

/**
 * The design-system Select renders its options as non-focusable divs, so picking
 * one leaves focus on `<body>` and the next Tab restarts from the first field.
 * Refocus the select header — matched by the `role="button"` the design system
 * puts on it — so keyboard navigation continues to the next field.
 */
export const FormSelect: React.FC<SelectProps> = ({
  onItemChange,
  setMaxHeight = DEFAULT_MAX_HEIGHT,
  ...rest
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleItemChange = (value: unknown) => {
    onItemChange(value);

    window.requestAnimationFrame(() => {
      wrapperRef.current
        ?.querySelector<HTMLElement>('[role="button"]')
        ?.focus();
    });
  };

  return (
    <div ref={wrapperRef}>
      <Select
        {...rest}
        setMaxHeight={setMaxHeight}
        onItemChange={handleItemChange}
      />
    </div>
  );
};
